import { useQuery } from '@apollo/client';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import React, { FC, useEffect, useMemo, useState } from 'react';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import {
  ReceiptType,
  useButton,
  useCurrency,
  useI18n,
  useLicenses,
  useLogger,
  useStaffCan,
} from '~Hooks';
import { useReceiptCopy } from '~Hooks/useReceiptCopy';
import { HistoryItemType } from '~Scenes/Handy/Scenes/History/Order';
import {
  RefundedInfoBox,
  ButtonsWrapper,
  SkeletonItem,
} from '~Scenes/Handy/Scenes/History/Order/styles';
import { COLORS, Input, ModalContent } from '~Scenes/Handy/styles';
import CopyToTable from '~Scenes/History/Components/CopyToTable';
import { TablesAndFloorPlansQuery_venue_floorPlans_tables as TableType } from '~Scenes/Handy/__queries__';
import Refunds from '~Scenes/Refunds';
import { isRefundPossible } from '~Scenes/Refunds/utils';
import { BlackButton, BlueButton, Box, Button, RedButton } from '~Styles';
import { combineArticles } from '~Utils/combineArticles';
import { GET_ORDER } from './queries';
import Heading from '~ComponentsImproved/Typography/Heading';
import printIcon from '~Assets/icons/print.svg';
import mailIcon from '~Assets/icons/mail.svg';
import refundIcon from '~Assets/icons/replay.svg';
import { GetOrder, GetOrderVariables } from './__queries__';
import styled from '@emotion/styled';
import Icon from '~ComponentsImproved/Icon';
import { DateTime } from 'luxon';
import { capitalize } from 'lodash';
import Loader from '~ComponentsImproved/Loader';
import { handyBreakpoint } from '~Styles/breakpoints';

const Wrapper = styled(Box)`
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  max-width: 600px;
  margin-bottom: 20px; // this because we ignore padding bottom in the parent to enable scroll to bottom for the left section
  border: 1px solid var(--colorBlueGray700);
  padding: 16px;
  width: 100%;
  margin: 0 auto;

  button {
    word-break: break-word;
  }

  ${handyBreakpoint} {
    border: none;
    background: transparent;
  }
`;

const Header = styled(Box)`
  ${handyBreakpoint} {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const OrderItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OrderItemWrapper = styled.div<{ isRefunded?: boolean }>`
  color: var(--colorWhite);
  letter-spacing: 0.24px;
  font-size: 14px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--colorBlueGray1000);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OrderItemName = styled.div`
  flex: 1;
  display: flex;
  gap: 8px;
  align-items: center;
  padding-right: 8px;
`;

const VariationsList = styled.div`
  font-size: 12px;
  margin-top: 4px;
  color: var(--colorBlueGray500);
`;

const StrikeThrough = styled.span`
  text-decoration: line-through;
  opacity: 0.7;
  margin-right: 8px;
`;

const TotalRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const RefundedTag = styled.span`
  border: 1px solid var(--colorUIRed);
  border-radius: 2px;
  padding: 2px 4px;
  color: var(--colorUIRed);
  margin-right: 8px;
  font-size: 12px;
  height: 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
`;

const RefundedItemActions = styled.div`
  display: flex;
  align-items: center;
`;

const PrintRefundIconButton = styled(Button)`
  background: transparent;
  color: var(--colorUIRed);
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  background-color: rgba(245, 65, 110, 0.1);
  height: 24px;
  min-height: 24px;
  width: 32px;
  min-width: 32px;
  margin-right: 8px;
`;

const DetailsSection = styled.div`
  margin-top: 24px;
`;

interface Props {
  orderId: string;
  onHandyCopyOrderComplete?: (table: TableType, chequeId: string) => void;
}

const HistoryOrder: FC<Props> = ({ orderId, onHandyCopyOrderComplete }) => {
  const logger = useLogger('history');
  const { i18n } = useI18n();
  const currency = useCurrency();
  const receiptCopy = useReceiptCopy();
  const { isShowing: refundsModalShowing, toggle: toggleRefundsModal } = useModal();
  const { isShowing: emailModalIsShowing, toggle: toggleEmailModal } = useModal();
  const [receiptEmail, setReceiptEmail] = useState<string | undefined>();
  const staffCan = useStaffCan();
  const [receiptCopyPrinted, setReceiptCopyPrinted] = useState(false);

  const orderQuery = useQuery<GetOrder, GetOrderVariables>(GET_ORDER, {
    skip: !orderId,
    fetchPolicy: 'no-cache',
    variables: {
      id: orderId!,
    },
  });

  const order = orderQuery.data?.order;

  const refundPossible = useMemo(() => {
    // Wait for order to be loaded before checking refund possibility
    if (!order) return false;
    return isRefundPossible(order);
  }, [order]);

  useEffect(() => {
    if (order && order?.user?.email) {
      setReceiptEmail(order.user.email);
    }
  }, [order]);

  const formattedItems: HistoryItemType[] = useMemo(
    () =>
      order?.items.map((item) => ({
        itemId: item.entry.id,
        title: i18n(item.entry.item.titleLang),
        amount: item.price.amount - item.price.discount,
        isRefunded: item.isRefunded,
        discount: item.price.discount,
        variations: item.variations.filter((variation) => variation.price > 0),
      })) || [],
    [order, i18n],
  );

  const combinedItems = useMemo(() => combineArticles(formattedItems), [formattedItems]);

  const refundedAt = order?.statusHistory.find(
    (item: { status: ORDER_STATUS; timeStamp: number }) => item.status === ORDER_STATUS.REFUNDED,
  );

  const orderHasBeenRefunded = order?.status === ORDER_STATUS.REFUNDED;

  const isMultiPaymentWithAdyen =
    order?.payment?.subPayments &&
    order?.payment?.subPayments?.length > 1 &&
    order?.payment?.subPayments.some((payment) => payment.type === 'ADYEN');

  const printReceiptCopybutton = useButton({
    onClick: async () => {
      if (!order) {
        return;
      }
      logger.info('Printing receipt copy', { orderId: order?.id, easyId: order?.easyId });

      await receiptCopy.printReceiptCopy({
        order,
        type: ReceiptType.Copy,
        isCopy: true,
      });

      setReceiptCopyPrinted(true);
    },
    disabled: orderHasBeenRefunded || order?.hasPrintedReceiptCopy || receiptCopyPrinted,
    disabledToast: i18n('History.PrintReceiptCopy.OnlyOne'),
  });

  const emailReceiptCopybutton = useButton({
    onClick: () => {
      toggleEmailModal();
    },
  });

  const refundButton = useButton({
    onClick: () => {
      logger.info('Opening refund modal', {
        orderId: order?.id,
        easyId: order?.easyId,
        orderStatus: order?.status,
        refundPossible,
      });
      toggleRefundsModal();
    },
    disabled: !staffCan.REFUND || !refundPossible || orderQuery.loading || isMultiPaymentWithAdyen,
    disabledToast: isMultiPaymentWithAdyen
      ? i18n('Refunds.Disabled.MultiPayment')
      : order?.status === ORDER_STATUS.REFUNDED
      ? i18n('Refunds.Disabled.AlreadyRefunded')
      : order?.status !== ORDER_STATUS.COMPLETED
      ? i18n('Refunds.Disabled.OrderNotCompleted')
      : !staffCan.REFUND
      ? i18n('Refunds.Disabled.StaffNotAllowed')
      : undefined,
  });

  const printRefundReceiptCopybutton = useButton({
    onClick: () => {
      if (!order) {
        return;
      }
      logger.info('Printing refund receipt copy', { orderId: order?.id, easyId: order?.easyId });
      receiptCopy.printReceiptCopy({
        order,
        type: ReceiptType.Refund,
        isCopy: true,
      });
    },
  });

  const sendEmailButton = useButton({
    onClick: () => {
      if (!order || !receiptEmail) {
        return;
      }

      receiptCopy.emailReceiptCopy({
        order,
        email: receiptEmail,
      });

      logger.info('Sending receipt copy via email', {
        orderId: order?.id,
        easyId: order?.easyId,
        email: receiptEmail,
      });

      // Wait one second before closing the modal
      setTimeout(() => {
        toggleEmailModal();
      }, 1000);
    },
  });

  // Function to print refund receipt for a specific item
  const printItemRefundReceipt = (itemId: string) => {
    if (!order) return;
    logger.info('Printing refund receipt for individual item', { itemId, orderId: order.id });
    receiptCopy.printReceiptCopy({
      order,
      type: ReceiptType.Refund,
      isCopy: true,
      itemIds: [itemId], // Pass the specific item ID as an array
    });
  };

  const formatOrderType = (type: string) => {
    switch (type) {
      case 'EAT_HERE':
        return i18n('ServiceType.EatHere');
      case 'TAKE_AWAY':
        return i18n('ServiceType.TakeAway');
      case 'DELIVERY':
        return i18n('ServiceType.Delivery');
      default:
        return type.toLowerCase().replace('_', ' ');
    }
  };

  const formatPaymentType = (type: string) => {
    switch (type) {
      case 'CASH':
        return i18n('PaymentType.Cash');
      case 'ADYEN':
        return i18n('PaymentType.Card');
      case 'IZETTLE':
        return i18n('PaymentType.Card');
      case 'INVOICE':
        return i18n('PaymentType.Invoice');
      case 'PRINTED_INVOICE':
        return i18n('PaymentType.PrintedInvoice');
      case 'ADYEN_MANUELL':
        return i18n('PaymentType.AdyenManuell');
      case 'GIFT_CARD':
        return i18n('PaymentType.GiftCard');
      case 'MICRODEB':
        return i18n('PaymentType.GiftCard');
      case 'RETAIN24':
        return i18n('PaymentType.GiftCard');
      case 'BAEMINGO_GIFTCARD':
        return i18n('PaymentType.BaemingoGiftCard');
      default:
        return capitalize(type.toLowerCase().replace('_', ' '));
    }
  };

  if (orderQuery.loading) {
    return (
      <Wrapper>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{ height: '400px' }}>
          <Loader size="sm" color="#fff" />
        </Box>
      </Wrapper>
    );
  }

  if (!order) {
    logger.warn('Order not found', { orderId });
    return (
      <Box padding="medium">
        <Paragraph level={2} color="var(--colorWhite)">
          {i18n('Error.SomethingWrong')}
        </Paragraph>
      </Box>
    );
  }

  const paymentsList = order.payment.subPayments.length
    ? order.payment.subPayments
    : [order.payment];

  return (
    <Wrapper>
      <>
        <Header display="flex" alignItems="center" gap="xs" justifyContent="space-between">
          <Box display="flex" gap="small">
            <Heading level={2} color="var(--colorWhite)">
              {`${i18n('History.Order')} #${order?.easyId ?? ''}`}
            </Heading>
            <CopyToTable
              order={order}
              onHandyComplete={(table, chequeId) => {
                if (onHandyCopyOrderComplete) {
                  onHandyCopyOrderComplete(table, chequeId);
                }
              }}
            />
          </Box>
          <Paragraph level={4} color="var(--colorBlueGray500)">
            {order.origin + ', ' + formatOrderType(order.type)}
          </Paragraph>
        </Header>

        {orderHasBeenRefunded && refundedAt && (
          <RefundedInfoBox>
            {i18n('Refunds.Refunded')}
            {': '}
            {DateTime.fromMillis(refundedAt.timestamp).toFormat('yyyy-MM-dd HH:mm')}
          </RefundedInfoBox>
        )}

        <ButtonsWrapper>
          <BlackButton {...printReceiptCopybutton}>
            <Icon src={printIcon} color="var(--colorWhite)" height="24px" />
            {i18n('History.PrintReceiptCopy')}
          </BlackButton>

          <BlackButton {...emailReceiptCopybutton}>
            <Icon src={mailIcon} color="var(--colorWhite)" height="24px" />
            {i18n('History.EmailReceiptCopy')}
          </BlackButton>

          {order?.status === ORDER_STATUS.REFUNDED && (
            <BlackButton {...printRefundReceiptCopybutton}>
              <Icon src={printIcon} color="var(--colorWhite)" height="24px" />
              {i18n('History.PrintRefundReceiptCopy')}
            </BlackButton>
          )}

          {order?.status !== ORDER_STATUS.REFUNDED && (
            <RedButton {...refundButton}>
              <Icon src={refundIcon} color="var(--colorWhite)" height="24px" />
              {i18n('History.Refund')}
            </RedButton>
          )}
        </ButtonsWrapper>

        {/* Order items, list similar to the list in the Cart */}
        <Box marginTop="medium">
          <OrderItemsList>
            {orderQuery.loading &&
              Array.from({ length: 3 }).map((_, index) => <SkeletonItem key={index} />)}
            {combinedItems.map((item) => {
              const count = 'count' in item ? item.count : 1;
              const variations = item.variations?.map((v) => v.name) ?? [];
              const isRefunded = 'isRefunded' in item ? item.isRefunded : false;

              return (
                <OrderItemWrapper key={item.itemId} isRefunded={isRefunded}>
                  <Box
                    display="flex"
                    alignItems="center"
                    flexDirection="row"
                    justifyContent="space-between">
                    <OrderItemName>
                      <span>{count}x</span> {item.title}
                    </OrderItemName>
                    {isRefunded && (
                      <RefundedItemActions>
                        <RefundedTag>{i18n('Refunds.Refunded')}</RefundedTag>
                        {/* Show print icon for individually refunded items, but not if the entire order is refunded */}
                        {order?.status !== ORDER_STATUS.REFUNDED && (
                          <PrintRefundIconButton
                            onClick={() => printItemRefundReceipt(item.itemId)}
                            onTouchStart={() => printItemRefundReceipt(item.itemId)}
                            title={i18n('History.PrintRefundReceiptCopy')}>
                            <Icon
                              src={printIcon}
                              color="var(--colorUIRed)"
                              height="16px"
                              width="16px"
                            />
                          </PrintRefundIconButton>
                        )}
                      </RefundedItemActions>
                    )}
                    <div>
                      {item.discount && item.amount ? (
                        <StrikeThrough>
                          {currency.format(item.amount + item.discount)}
                        </StrikeThrough>
                      ) : null}
                      {item.amount ? currency.format(item.amount) : ''}
                    </div>
                  </Box>
                  {variations.length > 0 && (
                    <VariationsList>{variations.join(', ')}</VariationsList>
                  )}
                </OrderItemWrapper>
              );
            })}
          </OrderItemsList>
          <Box marginTop="xs">
            {/* Total discount */}
            {order.items.reduce((sum, item) => sum + item.price.discount, 0) > 0 && (
              <TotalRow>
                <Paragraph level={4} color="var(--colorWhite)">
                  {i18n('History.Discount')}
                </Paragraph>
                <Paragraph level={4} color="var(--colorWhite)">
                  {currency.format(
                    order.items.reduce((sum, item) => sum + item.price.discount, 0),
                  )}
                </Paragraph>
              </TotalRow>
            )}

            {/* Total tip */}
            <TotalRow>
              <Paragraph level={4} color="var(--colorWhite)">
                {i18n('History.Tip')}
              </Paragraph>
              <Paragraph level={4} color="var(--colorWhite)">
                {currency.format(order.payment.tip || 0)}
              </Paragraph>
            </TotalRow>

            {/* Total price */}
            <TotalRow>
              <Heading level={4} color="var(--colorWhite)">
                {i18n('History.Total')}
              </Heading>
              <Heading level={4} color="var(--colorWhite)">
                {currency.format(order.payment.amount)}
              </Heading>
            </TotalRow>
          </Box>
        </Box>

        <DetailsSection>
          <Box marginBottom="medium">
            <Paragraph level={4} color="var(--colorBlueGray500)" bottom={4}>
              {i18n('History.PaymentDetails')}
            </Paragraph>
            {paymentsList.map((payment, index) => (
              <TotalRow key={index}>
                <Paragraph level={4} color="var(--colorWhite)">
                  {formatPaymentType(payment.type)}
                </Paragraph>
                <Paragraph level={4} color="var(--colorWhite)">
                  {i18n('History.Tip')}: {currency.format(payment.tip || 0)}
                </Paragraph>
                <Paragraph
                  level={4}
                  color="var(--colorWhite)"
                  style={{
                    minWidth: '80px',
                    textAlign: 'right',
                  }}>
                  {currency.format(payment.amount)}
                </Paragraph>
              </TotalRow>
            ))}
          </Box>

          {order.user.role === 'REGULAR' && (
            <Box marginBottom="medium">
              <Paragraph level={4} color="var(--colorBlueGray500)" bottom={4}>
                {i18n('History.UserDetails')}
              </Paragraph>
              <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
                {order.user?.name || '-'}
              </Paragraph>
              {order.user?.email && (
                <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
                  {order.user?.email || '-'}
                </Paragraph>
              )}
              {order.user?.phone && (
                <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
                  {order.user?.phone}
                </Paragraph>
              )}
              {order.user?.address && (
                <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
                  {order.user?.address}, {order.user?.zip || ''} {order.user?.city || ''}
                </Paragraph>
              )}
              {order.user?.orgNumber && (
                <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
                  {i18n('History.OrgNumber')}: {order.user?.orgNumber}
                </Paragraph>
              )}
            </Box>
          )}

          <Box marginBottom="medium">
            <Paragraph level={4} color="var(--colorBlueGray500)" bottom={4}>
              {i18n('History.OrderStatusHistory')}
            </Paragraph>
            {order.statusHistory.map((statusItem: any, index: number) => {
              return (
                <TotalRow key={index}>
                  <Paragraph level={4} color="var(--colorWhite)">
                    {statusItem.status}
                  </Paragraph>
                  <Paragraph level={4} color="var(--colorWhite)">
                    {statusItem.timestamp
                      ? DateTime.fromMillis(statusItem.timestamp).toFormat('yyyy-MM-dd HH:mm')
                      : i18n('History.UnknownTimestamp')}
                  </Paragraph>
                </TotalRow>
              );
            })}
          </Box>

          {order.payment?.metadata?.cardDetails &&
            order.payment?.metadata?.cardDetails.cardType !== null && (
              <Box marginBottom="medium">
                <Paragraph level={4} color="var(--colorBlueGray500)" bottom={4}>
                  {i18n('History.CardDetails')}
                </Paragraph>
                {Object.entries(order.payment.metadata.cardDetails).map(([key, value]) => {
                  if (key === '__typename') {
                    return null;
                  }

                  if (key === 'cardType') {
                    value = `${i18n('Receipt.CardType')}: ${value}`;
                  }

                  if (['tsi', 'tvr', 'aid'].includes(key) && value) {
                    value = `${key.toUpperCase()}: ${value}`;
                  }

                  return (
                    <Paragraph key={key} level={4} color="var(--colorWhite)" bottom={4}>
                      {value}
                    </Paragraph>
                  );
                })}
              </Box>
            )}
        </DetailsSection>

        {refundsModalShowing && (
          <Modal isShowing={refundsModalShowing} hide={toggleRefundsModal} noPadding>
            <Refunds
              orderId={order?.id ?? ''}
              selectedOrder={order}
              closeModal={toggleRefundsModal}
              onRefundCompleted={orderQuery.refetch}
            />
          </Modal>
        )}
        <Modal isShowing={emailModalIsShowing} hide={toggleEmailModal}>
          <ModalContent>
            <Heading level={3} color={COLORS.blueGray100}>
              {i18n('History.EmailReceiptCopy')}
            </Heading>
            <Input
              type="email"
              placeholder={i18n('History.Email.Placeholder')}
              value={receiptEmail}
              onChange={(e) => setReceiptEmail(e.currentTarget.value)}
            />
            <BlueButton {...sendEmailButton} marginTop="small">
              {i18n('General.Send')}
            </BlueButton>
          </ModalContent>
        </Modal>
      </>
    </Wrapper>
  );
};

export default HistoryOrder;
